import { useEffect, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useParams, useNavigate } from 'react-router-dom';
import { SelectChangeEvent, useMediaQuery, useTheme } from '@mui/material';
import { FuelType } from '@/types/price-comparison.types.ts';
import { StationsSortType } from '@/types/common.types';

import { useExportRegionalToExcelMutation, useGetRegionStationsDataQuery } from '../../../../services';
import { useSnackbar, useScrollSync } from '../../../../hooks';
import { PerformanceStatusIndicator, ErrorState, LoadingTable } from '../../../../components';
import { useRolePermissions } from '../../../../hooks/useRolePermissions';

import { StationsPageHeader, StationsPageControls, AreaStationsTable, StationsTableHeader } from '../../components';

import './RegionalStationsPage.css';

function RegionalStationsPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { region } = useParams<{ region: string }>();

  const { setSnackbarMessage } = useSnackbar();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { canViewSales, canExport } = useRolePermissions();

  const [sortBy, setSortBy] = useState<StationsSortType>('alphabetical');
  const [selectedBrand, setSelectedBrand] = useState('');

  const {
    data: { stationsData, stationsFuelTypes = [], brands: availableBrands = [], performanceStatus } = {},
    isLoading: stationsLoading,
    isFetching: stationsFetching,
    isSuccess: stationsSuccess,
    error: stationsError,
    refetch: refetchStations
  } = useGetRegionStationsDataQuery(region ? { region, sortBy, brandFilter: selectedBrand } : skipToken, {
    refetchOnMountOrArgChange: true
  });

  const [exportRegionalToExcel] = useExportRegionalToExcelMutation();

  const fuelTypes = stationsFuelTypes.map((fuel: FuelType): string => fuel.fuel_type);

  useEffect(() => {
    if (stationsError) {
      setSnackbarMessage('Помилка завантаження даних станцій', 'error');
    }
  }, [stationsError, setSnackbarMessage]);

  const { headerRef, bodyRef } = useScrollSync();

  const handleSortBy = (event: SelectChangeEvent<StationsSortType>) => {
    setSortBy(event.target.value as StationsSortType);
  };

  const handleBrandChange = (event: SelectChangeEvent<string>) => {
    setSelectedBrand(event.target.value);
  };

  const exportRegionalPrices = async () => {
    if (!region) return;
    await exportRegionalToExcel({ region, includeDiscounts: true })
      .unwrap()
      .then(() => setSnackbarMessage(`Файл для регіону ${region} успішно вивантажено`, 'success'))
      .catch(() => setSnackbarMessage('Помилка вивантаження файлу', 'error'));
  };

  const retryLoad = () => {
    refetchStations();
  };

  if (stationsError) {
    return (
      <div className="regional-stations-container">
        <ErrorState message="Помилка завантаження станцій" onRetry={retryLoad} />
      </div>
    );
  }

  if (stationsLoading || stationsFetching || !stationsData) {
    return <LoadingTable message="Завантаження станцій..." rows={10} columns={15} />;
  }

  return (
    <div className="regional-stations-container">
      {stationsSuccess && stationsData && (
        <PerformanceStatusIndicator performanceStatus={performanceStatus} onRefreshNeeded={retryLoad} />
      )}

      {/* Sticky Header */}
      <div className="regional-stations-sticky-header">
        <StationsPageHeader
          title={`Детальний перегляд АЗС - ${region}`}
          backPath="/pricing-regional"
          backLabel="Назад до ціноутворення"
          isMobile={isMobile}
          onNavigate={navigate}
        />
        <StationsPageControls
          sortBy={sortBy}
          brandFilter={selectedBrand}
          availableBrands={availableBrands}
          isMobile={isMobile}
          onSortChange={handleSortBy}
          onBrandChange={handleBrandChange}
          onExport={exportRegionalPrices}
          showExport={canExport}
          exportButtonClass="regional-stations-export-button"
        />
        <StationsTableHeader
          fuelTypes={fuelTypes}
          isMobile={isMobile}
          tableClass="regional-stations-table"
          scrollRef={headerRef}
          hideSales={!canViewSales}
        />
      </div>

      <AreaStationsTable
        stationsData={stationsData}
        fuelTypes={fuelTypes}
        isMobile={isMobile}
        sortBy={sortBy}
        selectedBrand={selectedBrand}
        tableClass="regional-stations-table"
        scrollRef={bodyRef}
        hideSales={!canViewSales}
      />
    </div>
  );
}

export default RegionalStationsPage;
